// Landing de captação (/cortesia): chamadas à API, UTMs, identificação do visitante e pixels.
// Mantido pequeno e sem axios/api.js de propósito: a LP recebe tráfego pago em celular.
const BASE = process.env.REACT_APP_BACKEND_URL || '';

export const optimizedImage = (src, w) => `${BASE}/api/media/opt?src=${encodeURIComponent(src)}&w=${w}`;
export const optimizedSrcSet = (src, widths = [480, 768, 1080, 1440]) =>
  widths.map((w) => `${optimizedImage(src, w)} ${w}w`).join(', ');

const request = async (path, options = {}) => {
  const res = await fetch(`${BASE}/api${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  let data = null;
  try { data = await res.json(); } catch { /* corpo vazio */ }
  if (!res.ok) {
    const err = new Error((data && data.detail) || 'Não foi possível concluir agora. Tente novamente.');
    err.status = res.status;
    throw err;
  }
  return data;
};

export const getLpConfig = () => request('/lp/config');

// ---- visitante e origem ----
const safe = (fn, fallback = null) => { try { return fn(); } catch { return fallback; } };

export const getVisitorId = () => safe(() => {
  let v = localStorage.getItem('fc_vid');
  if (!v) {
    v = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem('fc_vid', v);
  }
  return v;
}, `anon-${Math.random().toString(36).slice(2, 12)}`);

const UTM_KEYS = ['source', 'medium', 'campaign', 'content', 'term'];

// Guarda a origem do primeiro toque na sessão; novos parâmetros na URL sobrescrevem.
export const captureOrigin = () => safe(() => {
  const qs = new URLSearchParams(window.location.search);
  const found = {};
  UTM_KEYS.forEach((k) => { const v = qs.get(`utm_${k}`); if (v) found[k] = v; });
  const fbclid = qs.get('fbclid');
  const stored = JSON.parse(sessionStorage.getItem('fc_origin') || 'null') || { utm: {}, fbclid: '', referrer: document.referrer || '' };
  if (Object.keys(found).length) stored.utm = found;
  if (fbclid) stored.fbclid = fbclid;
  sessionStorage.setItem('fc_origin', JSON.stringify(stored));
  return stored;
}, { utm: {}, fbclid: '', referrer: '' });

const cookie = (name) => safe(() => {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
});

export const metaIds = () => {
  const origin = captureOrigin();
  let fbc = cookie('_fbc');
  if (!fbc && origin.fbclid) fbc = `fb.1.${Date.now()}.${origin.fbclid}`;
  return { fbp: cookie('_fbp'), fbc };
};

// ---- funil (contagem sem dado pessoal) ----
const sent = new Set();
export const trackStep = (type) => {
  if (sent.has(type)) return;
  sent.add(type);
  const origin = captureOrigin();
  fetch(`${BASE}/api/lp/track`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
    body: JSON.stringify({ vid: getVisitorId(), type, utm: origin.utm }),
  }).catch(() => {});
};

export const createLead = (form, extra) => {
  const origin = captureOrigin();
  return request('/lp/lead', {
    method: 'POST',
    body: JSON.stringify({
      ...form, utm: origin.utm, fbclid: origin.fbclid, vid: getVisitorId(),
      url: window.location.href, referrer: origin.referrer, ...metaIds(), ...extra,
    }),
  });
};

// ---- Pixel / GA4: respeitam "Do Not Track" e Global Privacy Control ----
export const trackingAllowed = () => safe(() => !(navigator.doNotTrack === '1' || window.doNotTrack === '1' || navigator.globalPrivacyControl === true), true);

let pixelsReady = false;
// Devolve true só na chamada que de fato ligou os pixels (a que já dispara o PageView); false se já estavam ligados ou o visitante recusou rastreio.
export const initPixels = ({ pixelId, ga4Id } = {}) => {
  if (pixelsReady || !trackingAllowed()) return false;
  pixelsReady = true;
  if (pixelId && !window.fbq) {
    /* eslint-disable */
    (function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
      t = b.createElement(e); t.async = true; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');
  }
  if (ga4Id && !window.gtag) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); }; // eslint-disable-line prefer-rest-params
    window.gtag('js', new Date());
    window.gtag('config', ga4Id, { send_page_view: true });
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga4Id)}`;
    document.head.appendChild(s);
  }
  return true;
};

// Navegação interna (sem recarregar a página) não gera PageView sozinha: a página de confirmação chama isto.
export const trackPageView = () => safe(() => {
  if (!trackingAllowed()) return;
  if (window.fbq) window.fbq('track', 'PageView');
  if (window.gtag) window.gtag('event', 'page_view');
});

// eventId (id do lead no servidor) deduplica o Pixel com a API de Conversões.
export const fireEvent = (name, { eventId, params } = {}) => safe(() => {
  if (!trackingAllowed()) return;
  const standard = name === 'Lead';
  if (window.fbq) {
    if (standard) window.fbq('track', 'Lead', params || {}, eventId ? { eventID: eventId } : undefined);
    else window.fbq('trackCustom', name, params || {});
  }
  if (window.gtag) {
    const ga = { Lead: 'generate_lead', FormStart: 'form_start', FormStep2: 'form_step2' }[name] || name;
    window.gtag('event', ga, params || {});
  }
});

// ---- máscara e utilidades de formulário ----
export const maskPhone = (value) => {
  const d = String(value || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

export const phoneValid = (value) => {
  const d = String(value || '').replace(/\D/g, '');
  if (d.length !== 11 && d.length !== 10) return false;
  const ddd = Number(d.slice(0, 2));
  if (ddd < 11 || ddd > 99) return false;
  if (d.length === 11 && d[2] !== '9') return false;
  return new Set(d.slice(2)).size > 1;
};

export const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

export const setMeta = ({ title, description, noindex }) => {
  if (title) document.title = title;
  const upsert = (attr, key, content) => {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
    el.setAttribute('content', content);
  };
  if (description) { upsert('name', 'description', description); upsert('property', 'og:description', description); }
  if (title) upsert('property', 'og:title', title);
  upsert('name', 'robots', noindex ? 'noindex,nofollow' : 'index,follow');
};
